import _ from 'underscore';
import HwpTvApi from '../../hwpTvApi';
import fetchData from '../../util/fetch';
import BaseGame from '../baseGame';
import { EVENTS } from '../baseGame.types';
import { ATTRIBUTES, HEADER_LABELS, NODE_ATTRIBUTES } from './advent.types';
import AdventNode from './adventNode';

/**
 * @typedef {import('./advent.types').AdventGameOptions} AdventGameOptions
 * @typedef {import('./advent.types').AdventNodeOptions} AdventNodeOptions
 */

export { ATTRIBUTES, EVENTS, HEADER_LABELS, NODE_ATTRIBUTES };

/**
 * @class
 * @extends BaseGame<AdventGameOptions, AdventNodeOptions, AdventNode>
 */

export default class AdventGame extends BaseGame {
	/** @type {AdventNodeOptions[]} */
	adventItems = [];

	get CN() {
		return AdventGame.name;
	}

	defaults() {
		return {
			...super.defaults(),
			[ATTRIBUTES.type]: 'advent',
			[ATTRIBUTES.startOnCurrentDate]: true,
			[ATTRIBUTES.labels]: {
				[HEADER_LABELS.score]: 'SCORE',
			},
			[ATTRIBUTES.btnCloseGameText]: '',
			[ATTRIBUTES.leaderboard]: {
				...super.defaults()[ATTRIBUTES.leaderboard],
				useNodeLeaderboards: true,
			},
		};
	}

	/** @param {AdventGameOptions} options */
	initialize(options) {
		this.logger.log(`[${this.initialize.name}]`, options);

		return fetchData(this.url())
			.then((response) => {
				// Set attributes from loaded data exept for the node data
				this.set(_.omit(response, ATTRIBUTES.nodes));

				// Determine if each node is unlocked or not and save to adventItems property
				/** @type {{nodes: AdventNodeOptions[]}} */
				const { nodes = [] } = response;
				const now = new Date();

				this.adventItems = nodes.map((node) => {
					const unlockTime = new Date(node.customerUnlockTime || '');
					node.isLocked = unlockTime > now;
					return node;
				});
			})
			.then(() => this.fetchGameProgress())
			.finally(() => {
				this.trigger(EVENTS.initialized, this);
				return this;
			});
	}

	getScore() {
		this.logger.log(`[${this.getScore.name}]`);

		return this.adventItems.reduce((score, item) => {
			if (item.selectedOptionId) {
				score += item.selectedOptionId;
			}
			return score;
		}, 0);
	}

	getPossibleScore() {
		this.logger.log(`[${this.getPossibleScore.name}]`);

		return this.adventItems.reduce(
			(score, item) =>
				score + this.getRoundOrGameAttribute(ATTRIBUTES.pointsPerQuestion, item),
			0,
		);
	}

	/**
	 * Returns a promise that will resolve when all media URLs from the game
	 * content have been succesfully loaded into the browser
	 */
	preload() {
		this.logger.log(`[${this.preload.name}]`);
		return this.preloadContent(this.adventItems);
	}

	/**
	 * @param {string} nodeId
	 * @param {number} score
	 */
	completeNode(nodeId, score) {
		this.logger.log(`[${this.completeNode.name}]`, { nodeId, score });

		const node = this.adventItems.find((item) => item.id === nodeId);
		if (node && !node.isCompleted) {
			// Only update if this node has not been completed
			node.selectedOptionId = score;
			node.isCompleted = true;

			// Track node complete
			this.sendGA4Event('AdventNodeComplete', { nodeId });

			// Record node score for later
			const gameId = this.get(ATTRIBUTES.gameId);
			if (gameId) {
				HwpTvApi.submitGameUserAnswer(gameId, nodeId, {
					userAnswer: score,
					engine: this.type,
				});

				// Record latest Advent game score
				const newGameScore = this.getScore();
				HwpTvApi.addGameScore(gameId, newGameScore, this.type);
			}
		} else {
			this.logger.warn('[Cannot mark node as complete]', { node });
		}
	}

	syncAnswers(storedAnswers) {
		this.logger.log(`[${this.syncAnswers.name}]`, { storedAnswers });

		// Update items that have some kind of answer data to indicate they are completed
		storedAnswers.map(({ questionId, answer: selectedOptionId }) => {
			const items = this.adventItems.find((item) => item.id === questionId);
			if (items) {
				return Object.assign(items, { selectedOptionId, isCompleted: true });
			}
			return null;
		});
	}

	// Return the index for the first unlocked & not-completed item, the last unlocked item, or -1
	getValidNodeId() {
		this.logger.log(`[${this.getValidNodeId.name}]`);

		let firstVirginId;
		let lastUnlockedId;
		const startOnCurrentDate = this.get(ATTRIBUTES.startOnCurrentDate);

		this.adventItems.forEach(({ id, isLocked, isCompleted = false }) => {
			if (!isLocked) {
				lastUnlockedId = id;
				if (!startOnCurrentDate && !isCompleted && !firstVirginId) {
					firstVirginId = id;
				}
			}
		});

		return firstVirginId || lastUnlockedId;
	}

	/**
	 * @param {string} nodeId
	 */
	getNodeGameData(nodeId) {
		this.logger.log(`[${this.getNodeGameData.name}]`, { nodeId });

		const gameData = this.adventItems.find((node) => node.id === nodeId);
		// engine/type are both used to allow multiple content sheets for same engine
		const { type = '', engine = '', pages = [] } = { ...gameData };
		const gameId = this.nodeIdToGameId(nodeId);
		const contentUrl = this.get(ATTRIBUTES.content)[type || engine];
		const adventPages = this.get(ATTRIBUTES.pages) || [];

		// Generate game data based on gathered data
		return {
			...gameData,
			type: engine || type,
			gameId,
			contentUrl,
			pages: [...pages, ...adventPages],
		};
	}

	getUnlockedNodes() {
		this.logger.log(`[${this.getUnlockedNodes.name}]`);

		return this.adventItems.filter(({ isLocked }) => !isLocked);
	}

	getUnlockedNodeGameData() {
		this.logger.log(`[${this.getUnlockedNodeGameData.name}]`);

		return this.getUnlockedNodes().map(({ id }) => this.getNodeGameData(id));
	}

	/**
	 * @param {string} nodeId
	 */
	nodeIdToGameId(nodeId) {
		this.logger.log(`[${this.nodeIdToGameId.name}]`, { nodeId });

		const gameId = this.get(ATTRIBUTES.gameId);
		if (!nodeId || !gameId) return undefined;

		const nodeData = this.adventItems.find((item) => item.id === nodeId);
		let { type, engine } = { ...nodeData };
		type = type ? `-${type}` : '';
		engine = engine ? `-${engine}` : '';

		return `${gameId}-node-${nodeId}${engine}${type}`;
	}

	destroy() {
		this.logger.log(`[${this.destroy.name}]`);

		this.off();

		this.adventItems.forEach((node) => _.result(node, 'off'));
		_.result(this.adventItems, 'clear');
		_.result(this.adventItems, 'off');
		this.adventItems = [];
	}
}
